import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/auth/AuthContext';
import {
  ArrowRightLeft,
  CheckCircle,
  FileText,
  TrendingUp,
  CalendarClock,
  Wallet,
  Calculator,
  Settings,
  BarChart3,
  // FilePen,  // Was used for standalone Custom Documents tile
  RefreshCw
} from 'lucide-react';
import './HomePage.css';

interface TileConfig {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  path: string | null;
  roles: string[];
  isPlaceholder?: boolean;
  subItems?: { titleKey: string; path: string }[];
}

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const tiles: TileConfig[] = [
    // Row 1: Trade Lifecycle
    {
      id: 'trade-booking',
      titleKey: 'navigation.tradeBooking',
      descriptionKey: 'home.tradeBookingDesc',
      icon: <ArrowRightLeft size={48} />,
      path: '/client/trade-booking',
      roles: ['client_admin', 'client_fo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'confirmations',
      titleKey: 'navigation.confirmations',
      descriptionKey: 'home.confirmationsDesc',
      icon: <CheckCircle size={48} />,
      path: '/confirmations',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'contracts',
      titleKey: 'navigation.contracts',
      descriptionKey: 'home.contractsDesc',
      icon: <FileText size={48} />,
      path: '/contract-comparison',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'valuation',
      titleKey: 'navigation.valuation',
      descriptionKey: 'home.valuationDesc',
      icon: <TrendingUp size={48} />,
      path: '/valuation',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'forward-expiries',
      titleKey: 'navigation.forwardExpiries',
      descriptionKey: 'home.forwardExpiriesDesc',
      icon: <CalendarClock size={48} />,
      path: '/forward-maturity',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'cashflow',
      titleKey: 'navigation.cashflowManagement',
      descriptionKey: 'home.cashflowDesc',
      icon: <Wallet size={48} />,
      path: '/cashflow-management',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'accounting',
      titleKey: 'navigation.accounting',
      descriptionKey: 'home.accountingDesc',
      icon: <Calculator size={48} />,
      path: '/accounting',
      roles: ['client_admin', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    {
      id: 'modifications',
      titleKey: 'navigation.modifications',
      descriptionKey: 'home.modificationsDesc',
      icon: <RefreshCw size={48} />,
      path: '/modifications',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']
    },
    // Row 2: General — Documents & Reports (consolidated from Custom Documents + Reports)
    {
      id: 'documents-reports',
      titleKey: 'navigation.documentsAndReports',
      descriptionKey: 'home.documentsAndReportsDesc',
      icon: <BarChart3 size={48} />,
      path: null,
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'],
      subItems: [
        { titleKey: 'navigation.customDocuments', path: '/custom-documents' },
        { titleKey: 'navigation.customReports', path: '/reports/custom-reports' },
        { titleKey: 'navigation.positions', path: '/reports/positions' },
        { titleKey: 'navigation.confirmationsReport', path: '/reports/confirmations' },
        { titleKey: 'navigation.accountingReport', path: '/reports/accounting' }
      ]
    },
    {
      id: 'administration',
      titleKey: 'navigation.administration',
      descriptionKey: 'home.administrationDesc',
      icon: <Settings size={48} />,
      path: null,
      roles: ['client_admin', 'client_acc_user'],
      subItems: [
        { titleKey: 'admin.tabs.confirmations', path: '/admin?tab=confirmations' },
        { titleKey: 'admin.tabs.accounts', path: '/admin?tab=accounts' },
        { titleKey: 'admin.tabs.workflow', path: '/admin?tab=workflow' },
        { titleKey: 'admin.tabs.fxgo', path: '/admin?tab=fxgo' },
        { titleKey: 'admin.tabs.email', path: '/admin?tab=email' },
        { titleKey: 'admin.tabs.accounting', path: '/admin?tab=accounting' },
        { titleKey: 'admin.tabs.groupings', path: '/admin?tab=groupings' },
        { titleKey: 'admin.tabs.imagecapture', path: '/admin?tab=imagecapture' },
        { titleKey: 'admin.tabs.customdocuments', path: '/admin?tab=customdocuments' },
        { titleKey: 'admin.tabs.contracts', path: '/admin?tab=contracts' },
        { titleKey: 'admin.tabs.settlementinstructions', path: '/admin?tab=settlementinstructions' },
        { titleKey: 'admin.tabs.forwardExpiries', path: '/admin?tab=forwardexpiries' },
        { titleKey: 'admin.tabs.users', path: '/admin?tab=users' },
        { titleKey: 'admin.tabs.valuation', path: '/admin?tab=valuation' }
      ].filter(item => {
        if (user?.role === 'client_acc_user') return item.path.includes('tab=accounting');
        return true;
      })
    }
  ];

  // Filter tiles based on user role
  const visibleTiles = tiles.filter(tile =>
    user?.role && tile.roles.includes(user.role)
  );

  // Separate into two rows
  const lifecycleTiles = visibleTiles.filter(tile =>
    ['trade-booking', 'confirmations', 'contracts', 'valuation', 'forward-expiries', 'cashflow', 'accounting', 'modifications'].includes(tile.id)
  );
  const generalTiles = visibleTiles.filter(tile =>
    ['documents-reports', 'administration'].includes(tile.id)
  );

  const handleTileClick = (tile: TileConfig) => {
    if (tile.isPlaceholder || !tile.path) return;
    navigate(tile.path);
  };

  const handleSubItemClick = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(path);
  };

  const renderTile = (tile: TileConfig) => {
    const isDisabled = tile.isPlaceholder || (!tile.path && !tile.subItems);

    return (
      <div
        key={tile.id}
        className={`home-tile ${isDisabled ? 'disabled' : ''} ${tile.subItems ? 'has-subitems' : ''}`}
        onClick={() => handleTileClick(tile)}
      >
        <div className="tile-icon">
          {tile.icon}
        </div>
        <h3 className="tile-title">{t(tile.titleKey)}</h3>
        <p className="tile-description">{t(tile.descriptionKey)}</p>
        {tile.isPlaceholder && (
          <span className="tile-badge">{t('home.comingSoon')}</span>
        )}
        {tile.subItems && (
          <div className="tile-subitems">
            {tile.subItems.map((subItem, index) => (
              <button
                key={index}
                className="tile-subitem"
                onClick={(e) => handleSubItemClick(subItem.path, e)}
              >
                {t(subItem.titleKey)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>{t('home.welcome')}</h1>
        <p>{t('home.subtitle')}</p>
      </div>

      {lifecycleTiles.length > 0 && (
        <div className="tile-section">
          <h2 className="section-title">{t('home.tradeLifecycle')}</h2>
          <div className="tile-row lifecycle-row">
            {lifecycleTiles.map(renderTile)}
          </div>
        </div>
      )}

      {generalTiles.length > 0 && (
        <div className="tile-section">
          <h2 className="section-title">{t('home.general')}</h2>
          <div className="tile-row general-row">
            {generalTiles.map(renderTile)}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
